class SProductForm extends HTMLElement {
  #ac;
  #abortController;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('submit', (event) => {
      if (!this.#shouldHandleSubmit(event)) return;

      event.preventDefault();
      this.#submit();
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#abortController?.abort();
  }

  #shouldHandleSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return false;

    const actionPath = this.#pathWithoutJsonSuffix(form.getAttribute('action'));
    const cartAddPath = this.#pathWithoutJsonSuffix(window.Theme?.routes?.cart_add_url);
    if (!actionPath || !cartAddPath || actionPath !== cartAddPath) return false;

    const submitter = event.submitter;
    if (submitter instanceof HTMLElement && !submitter.matches('button[name="add"], input[name="add"]')) {
      return false;
    }

    return true;
  }

  #pathWithoutJsonSuffix(value) {
    if (!value) return null;

    try {
      return new URL(value, window.location.origin).pathname.replace(/\.js$/, '');
    } catch {
      return null;
    }
  }

  async #submit() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const line = this.#buildLineInput();
    if (!line) {
      // No variant selected: nothing to submit. The submit button is disabled
      // in this state so this is a defensive guard.
      return;
    }

    this.setAttribute('aria-busy', 'true');

    try {
      await window.Shopify.actions.updateCart(
        { lines: [line] },
        {
          event: { context: 'product' },
          signal,
        }
      );

      // Check signal.aborted BEFORE clearing aria-busy. If a fresh submit
      // raced ahead, the newer submit has already re-set aria-busy='true';
      // clearing it here would briefly drop the busy state while the newer
      // request is still in flight.
      if (signal.aborted) return;
      this.removeAttribute('aria-busy');

      // Success / userError announcement (#sr-announcer + toast) is owned by
      // standard-actions-override. Don't write to [data-add-to-cart-status]
      // here — both are aria-live="polite" and would announce twice.
    } catch (error) {
      if (signal.aborted) return;
      this.removeAttribute('aria-busy');
    }
  }

  #buildLineInput() {
    const form = this.querySelector('form');
    const variantId = String(form?.elements.id?.value || '').trim();

    if (!variantId) return null;

    const quantityValue = Number.parseInt(String(form?.elements.quantity?.value || '1'), 10);
    const quantity = Number.isFinite(quantityValue) && quantityValue > 0 ? quantityValue : 1;

    return {
      merchandiseId: variantId,
      quantity,
    };
  }
}

if (!customElements.get('s-product-form')) {
  customElements.define('s-product-form', SProductForm);
}
